'use client'

import { useVideoAutoplay } from '@/hooks/common/use-video-autoplay'
import { getHomeHeroMobileN7ImageUrl } from '@/components/common/home/hero-banner-image-params'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn } from '@/lib/utils'
import { TBannerSlide } from '@/types/home'
import Image from 'next/image'
import { useRef } from 'react'

type HeroBannerVideoSlideProps = {
  slide: TBannerSlide
  /** Zero-based index within the carousel. Controls LCP priority. */
  index: number
  isMobile: boolean
  trackingValue: string
  finalUrl: string
}

/**
 * Image-first, video-second hero slide.
 *
 * Render order:
 *   1. <Image> (poster) — always rendered, LCP asset for index 0.
 *   2. <video>          — in DOM from SSR for zero CLS, but src is withheld
 *                         until the hook fires (intersection + idle frame).
 *   3. Once play() resolves, the poster fades out via opacity transition.
 *
 * The video element never blocks initial render or LCP.
 */
export function HeroBannerVideoSlide({
  slide,
  index,
  isMobile,
  trackingValue,
  finalUrl,
}: HeroBannerVideoSlideProps) {
  const videoRef = useRef<HTMLVideoElement>(null)

  // Determine which video src to use, if any.
  // Mobile falls back to image-only if no mb_video_url is provided.
  const resolvedVideoSrc = isMobile
    ? (slide.mb_video_url ?? null)
    : (slide.video_url ?? null)

  const { containerRef, isVideoPlaying } = useVideoAutoplay({
    videoRef,
    enabled: resolvedVideoSrc !== null,
  })

  const isLcpSlide = index === 0
  const overlayOpacity = slide.overlay_opacity ?? 0.3

  // Width must match the ReactDOM.preload hint in page.tsx (1440w) — otherwise
  // the browser fetches the full-resolution CDN original instead of the
  // preloaded/resized asset (see same fix in hero-banner.tsx).
  const posterSrc = isMobile
    ? getHomeHeroMobileN7ImageUrl(slide.mb_image)
    : ImageHelper.getImageUrl(slide.image, '', 1440)

  return (
    // Fixed-dimension wrapper prevents CLS. Aspect ratio matches the desktop
    // image dimensions used throughout the existing HeroBanner.
    <div
      ref={containerRef}
      className={cn(
        'relative w-full overflow-hidden',
        isMobile ? 'aspect-[1440/590]' : 'aspect-[1920/787]'
      )}
    >
      {/* ── Poster image ──────────────────────────────────────────────────────
          Always rendered. LCP element for the first slide.
          Fades out once the video starts playing, but stays in DOM as
          fallback for browsers that block autoplay.
      ─────────────────────────────────────────────────────────────────────── */}
      <Image
        src={posterSrc}
        alt={
          isMobile
            ? `Promotional banner ${index + 1} - mobile view`
            : `Promotional banner ${index + 1} - desktop view`
        }
        fill
        className={cn(
          'object-cover transition-opacity duration-700',
          // Keep poster fully visible until video confirms it is playing.
          isVideoPlaying ? 'opacity-0' : 'opacity-100'
        )}
        priority={isLcpSlide}
        loading={isLcpSlide ? 'eager' : 'lazy'}
        fetchPriority={isLcpSlide ? 'high' : 'auto'}
        sizes={
          isMobile
            ? '(max-width: 768px) 100vw, 768px'
            : '(min-width: 1024px) 100vw, 1920px'
        }
        quality={80}
      />

      {/* ── Video ─────────────────────────────────────────────────────────────
          Rendered in the DOM from SSR so the layout slot is reserved (CLS=0).
          src is intentionally absent — the hook assigns it via data-src once
          IntersectionObserver fires and the browser has idle cycles.
          `preload="none"` is a belt-and-suspenders hint for browsers that
          read the attribute before the hook fires (e.g. during SSR hydration).
      ─────────────────────────────────────────────────────────────────────── */}
      {resolvedVideoSrc !== null && (
        <video
          ref={videoRef}
          data-src={resolvedVideoSrc}
          className={cn(
            'absolute inset-0 h-full w-full object-cover transition-opacity duration-700',
            isVideoPlaying ? 'opacity-100' : 'opacity-0'
          )}
          muted
          loop
          playsInline
          preload='none'
          aria-hidden='true'
        />
      )}

      {/* ── Dark overlay ──────────────────────────────────────────────────────
          Ensures heading/CTA text remains readable over any media.
          Opacity is configurable per slide via slide.overlay_opacity.
      ─────────────────────────────────────────────────────────────────────── */}
      {(slide.heading || slide.cta_button_text) && (
        <div
          className='pointer-events-none absolute inset-0 z-[1]'
          style={{ backgroundColor: `rgba(0,0,0,${overlayOpacity})` }}
          aria-hidden='true'
        />
      )}

      {/* ── Text / CTA layer ──────────────────────────────────────────────── */}
      {(slide.heading || slide.cta_button_text) && (
        <div className='absolute inset-0 z-[2] flex flex-col items-center justify-center gap-4 px-4 text-center text-white'>
          {slide.heading && (
            <h2 className='text-2xl font-bold drop-shadow-md md:text-4xl'>
              {slide.heading}
            </h2>
          )}
          {slide.descriptions && (
            <p className='max-w-xl text-sm drop-shadow md:text-base'>
              {slide.descriptions}
            </p>
          )}
          {slide.cta_button_text && (
            <a
              href={finalUrl}
              target={slide.link_target}
              className='mt-2 inline-block rounded-full bg-white px-8 py-3 text-sm font-semibold text-black transition-colors hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black'
              ga-tracking-value={trackingValue}
            >
              {slide.cta_button_text}
            </a>
          )}
        </div>
      )}

      {/* ── Click-through link (no text/CTA variant) ──────────────────────── */}
      {!slide.heading && !slide.cta_button_text && (
        <a
          href={finalUrl}
          target={slide.link_target}
          className='absolute inset-0 z-[2]'
          aria-label={`View promotion ${index + 1}`}
          ga-tracking-value={trackingValue}
        />
      )}
    </div>
  )
}
